import React from "react";
import { Phone, Mail, HardHat, MapPinned, Layers, Check } from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import { PageHero } from "./Services";
import ApplyForm from "../components/ApplyForm";
import { brand, afterRoof } from "../data/site";
import { careerRoles } from "../data/careers";

const reasons = [
  { icon: HardHat, title: "Build Work That Lasts", text: "Quality craftsmanship is the standard on every Pepla job, and we want people who take pride in doing it right." },
  { icon: Layers, title: "Every Roof System", text: "Tile, shingle, foam and flat — crews here learn the whole range, not just one system." },
  { icon: MapPinned, title: "Based in Phoenix", text: "We work out of Phoenix, and our jobs span every city in Maricopa County." },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
    { "@type": "ListItem", position: 2, name: "Join Our Team", item: `${SITE_URL}/join-our-team` },
  ],
};

const JoinOurTeam = () => (
  <div>
    <SEO
      title="Careers at Pepla Roofing | Phoenix, AZ"
      description="Apply to work with Pepla Roofing in Phoenix, AZ. Roofing, inspection, sales and office roles."
      jsonLd={jsonLd}
    />
    <PageHero
      eyebrow="Careers"
      title="Join Our Team"
      subtitle="We're always looking for skilled, reliable people who take pride in doing roofs right."
      image={afterRoof}
    />

    <section className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2 space-y-8">
          <div>
            <span className="kicker">Why Pepla</span>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mt-3 mb-4">Grow With Us</h2>
            <div className="brand-line mb-6" />
            <p className="text-muted-foreground leading-relaxed">
              Whether you're an experienced installer, a crew lead, a sales professional or just getting started in the
              trades, tell us about yourself. We review every application.
            </p>
          </div>

          <div className="space-y-4">
            {reasons.map((r) => (
              <div key={r.title} className="flex gap-4 bg-secondary rounded-xl p-5">
                <span className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center shrink-0">
                  <r.icon size={22} className="text-ink" />
                </span>
                <div>
                  <h3 className="font-heading text-lg uppercase tracking-wide">{r.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{r.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div>
            <h3 className="font-heading text-lg uppercase tracking-wide mb-3">Roles We Hire For</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-x-4 gap-y-2">
              {careerRoles.filter((r) => r !== "Other").map((r) => (
                <li key={r} className="flex items-center gap-2 text-sm">
                  <span className="w-5 h-5 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
                    <Check size={12} className="text-brand" />
                  </span>
                  {r}
                </li>
              ))}
            </ul>
          </div>

          <div className="section-dark text-white rounded-2xl p-6 space-y-3">
            <p className="text-white/70 text-sm">Questions about working with us?</p>
            <a href={`tel:${brand.phoneRaw}`} className="flex items-center gap-3 font-heading text-xl hover:text-brand transition-colors">
              <Phone size={18} className="text-brand" /> {brand.phone}
            </a>
            <a href={`mailto:${brand.emailRaw || brand.email}`} className="flex items-center gap-3 text-sm hover:text-brand transition-colors">
              <Mail size={18} className="text-brand" /> {brand.email}
            </a>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="bg-card border border-ink/10 rounded-2xl p-6 md:p-8 shadow-sm">
            <h2 className="font-heading text-2xl uppercase tracking-wide mb-2">Apply Now</h2>
            <p className="text-muted-foreground text-sm mb-6">Fields marked * are required. Your application goes straight to our hiring team.</p>
            <ApplyForm />
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default JoinOurTeam;
